import { useState, useMemo } from 'react';
import { cn } from '@/utils/cn';
import type { FormationPersonSuggestion, PositionalAssignment } from '@/services/evaluationApi';

// ─── Per-person accent colors — mirrors PersonPicker palette ─────────────────

const PERSON_COLORS: { border: string; hex: string }[] = [
  { border: 'border-blue-400',   hex: '#60a5fa' },
  { border: 'border-green-400',  hex: '#4ade80' },
  { border: 'border-orange-400', hex: '#fb923c' },
  { border: 'border-purple-400', hex: '#c084fc' },
  { border: 'border-pink-400',   hex: '#f472b6' },
];

function colorFor(index: number) {
  return PERSON_COLORS[index % PERSON_COLORS.length];
}

// ─── Confidence dot ───────────────────────────────────────────────────────────

interface ConfidenceDotProps {
  confidence: number | null;
}

/**
 * Renders a small colored dot indicating suggestion confidence.
 * Green ≥ 0.70, yellow ≥ 0.40, red < 0.40. No dot when confidence is null.
 */
function ConfidenceDot({ confidence }: ConfidenceDotProps) {
  if (confidence === null) return null;

  const colorClass =
    confidence >= 0.70
      ? 'bg-green-400'
      : confidence >= 0.40
      ? 'bg-yellow-400'
      : 'bg-red-400';

  const label =
    confidence >= 0.70
      ? 'High confidence'
      : confidence >= 0.40
      ? 'Medium confidence'
      : 'Low confidence';

  return (
    <span
      className={cn('inline-block w-2.5 h-2.5 rounded-full flex-shrink-0', colorClass)}
      title={`${label} (${Math.round(confidence * 100)}%)`}
      aria-label={`${label}`}
      data-testid="confidence-dot"
    />
  );
}

// ─── PersonLabelCard ──────────────────────────────────────────────────────────

interface PersonLabelCardProps {
  person: FormationPersonSuggestion;
  templateLabels: string[];
  selectedLabel: string;
  isDuplicate: boolean;
  onChange: (label: string) => void;
}

function PersonLabelCard({
  person,
  templateLabels,
  selectedLabel,
  isDuplicate,
  onChange,
}: PersonLabelCardProps) {
  const { border, hex } = colorFor(person.person_index);

  return (
    <div
      className={cn(
        'flex flex-col gap-2 p-3 rounded-lg border-2 bg-[#0d1117]',
        isDuplicate ? 'border-red-500/60' : border
      )}
      style={!isDuplicate ? { boxShadow: `0 0 8px ${hex}33` } : undefined}
      data-testid={`person-label-card-${person.person_index}`}
    >
      {/* Thumbnail */}
      <div
        className={cn(
          'relative w-16 h-24 mx-auto rounded overflow-hidden border-2 flex items-center justify-center bg-[#1a1f28]',
          border
        )}
      >
        <img
          src={person.crop_data_url}
          alt={`Person ${person.person_index + 1} crop`}
          className="w-full h-full object-cover"
        />
        {/* Numbered badge */}
        <span
          className="absolute top-1 left-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white select-none"
          style={{
            backgroundColor: hex,
            boxShadow: '0 1px 4px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.25)',
          }}
          aria-hidden="true"
        >
          {person.person_index + 1}
        </span>
      </div>

      {/* Label + confidence */}
      <div className="text-center">
        <span className="text-xs font-medium text-text-primary">
          Person {person.person_index + 1}
        </span>
      </div>

      {/* Dropdown + confidence dot */}
      <div className="flex items-center gap-1.5">
        <select
          value={selectedLabel}
          onChange={(e) => onChange(e.target.value)}
          aria-label={`Position label for Person ${person.person_index + 1}`}
          className={cn(
            'flex-1 rounded-sm-control border bg-dark-elevated-1 text-text-primary px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-accent/50',
            isDuplicate
              ? 'border-red-500/60 focus:ring-red-500/50'
              : 'border-border-subtle'
          )}
        >
          <option value="">(none)</option>
          {templateLabels.map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </select>
        <ConfidenceDot confidence={person.suggestion_confidence} />
      </div>

      {/* Duplicate inline warning */}
      {isDuplicate && (
        <p
          className="text-[10px] text-red-400 leading-tight"
          role="alert"
          data-testid={`duplicate-warning-${person.person_index}`}
        >
          Label already used
        </p>
      )}
    </div>
  );
}

// ─── PositionalLabelPicker (main export) ─────────────────────────────────────

export interface PositionalLabelPickerProps {
  /** Detected persons with suggested labels from POST /api/evaluate/formation-suggest */
  persons: FormationPersonSuggestion[];
  /** All position labels the template defines — every label must be assigned exactly once */
  templateLabels: string[];
  /** Called when coach confirms the assignment; receives the final person→label mapping */
  onConfirm: (assignments: PositionalAssignment[]) => void;
}

/**
 * PositionalLabelPicker — lets the coach confirm or correct the auto-suggested
 * person→position-label assignments before a positional team-drill evaluation starts.
 *
 * Confirm button is enabled ONLY when every templateLabel has exactly one assignment
 * and no label is used more than once.
 */
export default function PositionalLabelPicker({
  persons,
  templateLabels,
  onConfirm,
}: PositionalLabelPickerProps) {
  // Initialize dropdown state from suggested_label, falling back to ''
  const [selections, setSelections] = useState<Record<number, string>>(() => {
    const init: Record<number, string> = {};
    for (const p of persons) {
      init[p.person_index] = p.suggested_label ?? '';
    }
    return init;
  });

  // Compute which person_indexes have duplicate labels (label used by >1 person)
  const duplicateIndexes = useMemo<Set<number>>(() => {
    const labelCounts: Record<string, number[]> = {};
    for (const [idxStr, label] of Object.entries(selections)) {
      if (!label) continue;
      const idx = Number(idxStr);
      if (!labelCounts[label]) labelCounts[label] = [];
      labelCounts[label].push(idx);
    }
    const dups = new Set<number>();
    for (const indexes of Object.values(labelCounts)) {
      if (indexes.length > 1) {
        for (const idx of indexes) dups.add(idx);
      }
    }
    return dups;
  }, [selections]);

  // Confirm is enabled when: all templateLabels are assigned exactly once + no dups
  const assignedLabels = useMemo(
    () => Object.values(selections).filter(Boolean),
    [selections]
  );

  const allLabelsAssigned = useMemo(() => {
    const assigned = new Set(assignedLabels);
    return templateLabels.every((lbl) => assigned.has(lbl));
  }, [assignedLabels, templateLabels]);

  const confirmEnabled = allLabelsAssigned && duplicateIndexes.size === 0;

  function handleChange(personIndex: number, label: string) {
    setSelections((prev) => ({ ...prev, [personIndex]: label }));
  }

  function handleConfirm() {
    if (!confirmEnabled) return;
    const assignments: PositionalAssignment[] = persons
      .map((p) => ({ person_index: p.person_index, position_label: selections[p.person_index] }))
      .filter((a) => a.position_label !== '');
    onConfirm(assignments);
  }

  return (
    <div
      className="rounded-xl border border-white/10 bg-[#0d1117] p-4 space-y-4"
      data-testid="positional-label-picker"
    >
      {/* Header */}
      <div className="flex items-start gap-2">
        <svg
          className="w-4 h-4 text-text-muted flex-shrink-0 mt-0.5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zm8 0a3 3 0 11-6 0 3 3 0 016 0zM5.5 14a4.5 4.5 0 019 0v.5H5.5V14zM1 14a5.5 5.5 0 0111 0v.5H1V14z" />
        </svg>
        <div>
          <p className="text-sm font-medium text-text-secondary">
            Assign formation positions
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            Each position label must be assigned to exactly one person. The confidence
            dot shows how sure the system is about each suggestion.
          </p>
        </div>
      </div>

      {/* Person cards */}
      <div
        className="flex flex-wrap gap-3"
        role="group"
        aria-label="Person position assignments"
      >
        {persons.map((person) => (
          <PersonLabelCard
            key={person.person_index}
            person={person}
            templateLabels={templateLabels}
            selectedLabel={selections[person.person_index] ?? ''}
            isDuplicate={duplicateIndexes.has(person.person_index)}
            onChange={(label) => handleChange(person.person_index, label)}
          />
        ))}
      </div>

      {/* Confidence legend */}
      <div className="flex items-center gap-4 text-[10px] text-text-muted">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-green-400" aria-hidden="true" />
          High (&ge;70%)
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-yellow-400" aria-hidden="true" />
          Medium (&ge;40%)
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-red-400" aria-hidden="true" />
          Low (&lt;40%)
        </span>
      </div>

      {/* Global duplicate warning (summary) */}
      {duplicateIndexes.size > 0 && (
        <p
          className="text-xs text-red-400"
          role="alert"
          data-testid="global-duplicate-warning"
        >
          Duplicate position labels detected. Each label must be assigned to exactly one person.
        </p>
      )}

      {/* Confirm button */}
      <button
        type="button"
        onClick={handleConfirm}
        disabled={!confirmEnabled}
        className="w-full py-2 px-4 rounded-lg text-sm font-semibold bg-accent text-text-inverse hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        data-testid="confirm-assignments-button"
      >
        Confirm assignments and evaluate
      </button>
    </div>
  );
}
